/**
 * Collection service for Shopify operations
 * Handles all collection-related API calls
 */

import { BaseShopifyService } from "../services/base";
import { GET_COLLECTION, GET_COLLECTIONS } from "./queries";
import {
  mapGetCollectionsToResponse,
  mapGetCollectionToResponse,
} from "./mappers";
import {
  ICollectionQueryRequest,
  ISingleCollectionRequest,
  IOptionsRequest,
  IResponse,
} from "../../../models";
import { formatResponse } from "../../../core/utils";

export class CollectionService extends BaseShopifyService {
  /**
   * Get collections based on query parameters
   */
  async getCollections(
    params: ICollectionQueryRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      // ✅ Validation & Defaults
      const DEFAULT_FIRST = 10;
      const MAX_FIRST = 100;

      const first =
        typeof params.first === "number" && params.first > 0
          ? Math.min(params.first, MAX_FIRST)
          : DEFAULT_FIRST;

      const query = options?.query || GET_COLLECTIONS;
      const variables = options?.variables || {
        ...params,
        first,
      };

      // ✅ Execute GraphQL query
      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error("GraphQL errors in GET_COLLECTIONS", undefined, {
          errors,
        });

        return formatResponse({
          success: false,
          message: "Failed to fetch collections due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data - now supports both nodes and edges
      if (
        !result?.data?.collections ||
        (!result.data.collections.nodes && !result.data.collections.edges)
      ) {
        return formatResponse({
          success: true,
          message: "Request completed successfully. No collections found.",
          data: [],
          meta: { pagination: undefined },
        });
      }

      return formatResponse({
        success: true,
        message: "Collections retrieved successfully.",
        data: mapGetCollectionsToResponse(result.data.collections),
        meta: {
          pagination: result.data.collections.pageInfo,
        },
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in getCollections", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while processing the request.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }

  /**
   * Get a collection by ID
   */
  async getCollection(
    params: ISingleCollectionRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      // ✅ Validation & Input Processing
      const { id, handle } = params || {};

      if (!id && !handle) {
        return formatResponse({
          success: false,
          message: "No collection ID or handle provided.",
        });
      }

      const query = options?.query || GET_COLLECTION;
      const variables: Record<string, any> = { ...(options?.variables || {}) };

      if (id) {
        const idStr = id.toString();
        variables.id = idStr.includes("gid://")
          ? idStr
          : `gid://shopify/Collection/${idStr}`;
      } else if (handle) {
        variables.handle = handle.toString();

        // Add sorting and pagination parameters
        if (params.sortKey) {
          variables.sortKey = params.sortKey;
          variables.reverse = params.reverse;
        }

        if (params.first) {
          variables.first = params.first;
        }

        if (params.after) {
          variables.after = params.after;
        }

        if (params.filters && params.filters.length > 0) {
          variables.filters = params.filters;
        }
      }

      // ✅ Execute GraphQL query
      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error("GraphQL errors in GET_COLLECTION", undefined, {
          errors,
        });

        return formatResponse({
          success: false,
          message: "Failed to fetch collection due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (!result?.data || !result.data.collection) {
        return formatResponse({
          success: true,
          message: "Request completed successfully. No collection found.",
          data: null,
          meta: undefined,
        });
      }

      // ✅ Normal success path - Use proper mapper with edges/nodes handling
      return formatResponse({
        success: true,
        message: "Collection retrieved successfully.",
        data: mapGetCollectionToResponse(result.data.collection),
        meta: {
          pagination: result.data.collection.products?.pageInfo,
          filters: result.data.collection.products?.filters,
        },
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in getCollection", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while processing the request.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }
}
